import { getRange } from "./range.util";
/** Get the date range for the last X days
 * Depending on the sign of the days parameter, the range will consider the whole day.
 * @param days - The number of days to include in the range
 * @param referenceDate - The reference date to use for the range (default: current date)
 */
export function getDateRangeFromDays(days, referenceDate = new Date()) {
    const mode = Math.sign(days) === 1 ? "future" : "past";
    const from = new Date(referenceDate);
    const to = new Date(referenceDate);
    if (mode === "future") {
        to.setDate(from.getDate() + days);
        to.setHours(23, 59, 59, 999);
        from.setHours(0, 0, 0, 0);
    }
    else {
        from.setDate(from.getDate() + days);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
    }
    return { from, to };
}
export const monthNames = [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
];
export function toDateString(date) {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date
        .getDate()
        .toString()
        .padStart(2, "0")}`;
}
export function getToday() {
    return toDateString(new Date());
}
const prevDays = new Map();
export const getPrevDay = (date) => {
    let prev = prevDays.get(date);
    if (prev) {
        return prev;
    }
    prev = dateAdd(date, { days: -1 });
    prevDays.set(date, prev);
    return prev;
};
const nextDays = new Map();
export const getNextDay = (date) => {
    let next = nextDays.get(date);
    if (next) {
        return next;
    }
    next = dateAdd(date, { days: 1 });
    nextDays.set(date, next);
    return next;
};
/**
 *
 * Get week number based on the [ISO week date system](https://en.wikipedia.org/wiki/ISO_week_date),
 * where each week starts with monday and the first week of the year
 * is the week that contains the first Thursday of the year.
 */
export function getWeekNumber(date) {
    const weekRange = getRange(date, "week");
    const thursday = dateAdd(weekRange.from, { days: 3 });
    const d = parseDateString(thursday);
    const firstDayOfYear = new Date(d.getFullYear(), 0, 1);
    const dayOfYear = Math.round((d.valueOf() - firstDayOfYear.valueOf()) / 86400000) + 1;
    return Math.ceil(dayOfYear / 7);
}
/**
 *
 * Get the week-based year based on the [ISO week date system](https://en.wikipedia.org/wiki/ISO_week_date),
 * i.e. the calendar year of the week's Thursday.
 */
export function getWeekYear(date) {
    const weekRange = getRange(date, "week");
    const thursday = dateAdd(weekRange.from, { days: 3 });
    return parseDateString(thursday).getFullYear();
}
export function getDayOfWeek(date) {
    const d = date instanceof Date ? date : parseDateString(date);
    return d?.getDay() || 0;
}
export function parseDateString(str) {
    const date = new Date(str);
    // invalid date
    if (isNaN(date.getTime())) {
        return null;
    }
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    return date;
}
export function getMonday(inp) {
    const date = parseDateString(inp);
    date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
    return toDateString(date);
}
export function getSunday(inp) {
    const date = parseDateString(inp);
    date.setDate(date.getDate() + ((7 - date.getDay()) % 7));
    return toDateString(date);
}
export function getYearRange(inp) {
    const date = parseDateString(inp);
    const from = new Date(date.getFullYear(), 0, 1);
    const to = new Date(date.getFullYear() + 1, 0, 1);
    return { from: toDateString(from), to: toDateString(to) };
}
export function getMonthRange(inp) {
    const date = parseDateString(inp);
    const from = new Date(date.getFullYear(), date.getMonth(), 1);
    const to = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    return { from: toDateString(from), to: toDateString(to) };
}
export function getWeekRange(inp) {
    const from = getMonday(inp);
    return {
        from,
        to: dateAdd(from, { days: 7 }),
    };
}
export function getDayRange(inp) {
    return { from: inp, to: dateAdd(inp, { days: 1 }) };
}
export function dateAdd(inp, { years = 0, months = 0, days = 0, weeks = 0 }) {
    const date = parseDateString(inp);
    if (!date) {
        throw new Error(`${inp} is not a valid date string!`);
    }
    if (weeks) {
        days += weeks * 7;
    }
    // Check number of days in the target month
    const daysInTargetMonth = new Date(date.getFullYear() + years, date.getMonth() + months + 1, 0).getDate();
    date.setFullYear(date.getFullYear() + years);
    // If target month has fewer days that current date, set the date to the maximum date in that month
    if (date.getDate() > daysInTargetMonth) {
        date.setDate(daysInTargetMonth);
    }
    date.setMonth(date.getMonth() + months);
    date.setDate(date.getDate() + days);
    return toDateString(date);
}
export function dateStringMin(a, b) {
    return a < b ? a : b;
}
export function dateStringMax(a, b) {
    return a > b ? a : b;
}
export function dateStringClamp(a, min, max) {
    return dateStringMax(min, dateStringMin(max, a));
}
export function firstDayOfMonth(d) {
    if (!d)
        return;
    return getMonthRange(d).from;
}
export function lastDayOfMonth(d) {
    if (!d)
        return;
    return getMonthRange(d).to;
}
export function lastDayOfPreviousMonth(d) {
    const monthRange = getMonthRange(d);
    return dateAdd(monthRange.from, { days: -1 });
}
// Get difference in days
export function dateSub(start, end) {
    return Math.round((parseDateString(end).getTime() - parseDateString(start).getTime()) / 24 / 60 / 60 / 1000);
}
export function getDateArray(from, to) {
    const res = [];
    let current = from;
    while (current < to) {
        res.push(current);
        current = getNextDay(current);
    }
    return res;
}
export function formatDate(date, fullYear = true) {
    const d = typeof date === "string" ? parseDateString(date) : date;
    if (!d) {
        return "Fehlerhaftes Datum";
    }
    return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1).toString().padStart(2, "0")}.${fullYear ? d.getFullYear() : d.getFullYear().toString().slice(-2)}`;
}
export function formatDateShort(date, numeric = false, includeYear = false) {
    const d = typeof date === "string" ? parseDateString(date) : date;
    if (!d) {
        return "Fehlerhaftes Datum";
    }
    let str = numeric
        ? `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1).toString().padStart(2, "0")}.`
        : `${d.getDate().toString().padStart(2, "0")}. ${monthNames[d.getMonth()].slice(0, 3)}`;
    if (includeYear) {
        str += " " + d.getFullYear().toString().slice(-2);
    }
    return str;
}
export function formatWeekDayShort(date) {
    const d = typeof date === "string" ? parseDateString(date) : date;
    return ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][d.getDay()];
}
export function formatWeekDay(date) {
    const d = typeof date === "string" ? parseDateString(date) : date;
    return ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"][d.getDay()];
}
export function formatMonth(date) {
    const d = typeof date === "string" ? parseDateString(date) : date;
    return `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
}
export function formatMonthShort(date, includeYear = true) {
    const d = typeof date === "string" ? parseDateString(date) : date;
    return `${monthNames[d.getMonth()].slice(0, 3)}${includeYear ? ` ${d.getFullYear()}` : ""}`;
}
export function isValidDateString(str) {
    if (!str)
        return false;
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(str))
        return false;
    try {
        return toDateString(new Date(str)) === str;
    }
    catch {
        return false;
    }
}
/**
 * Check if a date is within a given range (inclusive start, exclusive end)
 * @param date - The date to check
 * @param range - The {@link DateRange} where from is inclusive and to is exclusive
 * @returns true if the date is within the range, false otherwise
 */
export function isDateInRange(date, range) {
    if (!date)
        return false;
    const { from, to } = range;
    return date >= from && date < to;
}
//# sourceMappingURL=date.util.js.map